import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Minus, Plus, Star, Truck, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, money, formatApiErrorDetail } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ProductCard } from "@/components/ProductCard";
import { SEO } from "@/components/SEO";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ZoomImage = ({ src, alt }) => {
  const ref = useRef(null);
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState("center");

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setOrigin(`${x}% ${y}%`);
  };

  return (
    <div
      ref={ref}
      data-testid="product-zoom-image"
      className="relative aspect-[4/5] overflow-hidden bg-[#0A0A0A] cursor-zoom-in"
      onMouseEnter={() => setZoom(true)}
      onMouseLeave={() => setZoom(false)}
      onMouseMove={onMove}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover transition-transform duration-300"
        style={{ transform: zoom ? "scale(1.8)" : "scale(1)", transformOrigin: origin }}
      />
    </div>
  );
};

const ReviewSection = ({ product }) => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/reviews/${product.id}`).then(({ data }) => setReviews(data));
  }, [product.id]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/reviews/${product.id}`, form);
      setReviews((prev) => [data, ...prev]);
      setForm({ name: "", rating: 5, comment: "" });
      toast.success("Review submitted — thank you.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="border-t border-white/10 py-16" data-testid="reviews-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <p className="label-757 mb-3">Customer Reviews</p>
          <h2 className="font-display text-2xl font-semibold text-white mb-2">
            {product.rating} <span className="text-[#0FB5A3]">★</span>
          </h2>
          <p className="text-xs text-white/40 mb-8">{reviews.length} verified reviews</p>
          <div className="space-y-6 max-h-[420px] overflow-y-auto pr-3">
            {reviews.map((r) => (
              <div key={r.id} className="border border-white/10 p-5" data-testid="review-item">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-white font-display">{r.name}</p>
                  <div className="flex gap-0.5">
                    {[...Array(r.rating)].map((_, i) => (
                      <Star key={i} size={11} className="text-[#0FB5A3]" fill="#0FB5A3" />
                    ))}
                  </div>
                </div>
                <p className="text-sm text-white/60 leading-relaxed">{r.comment}</p>
                {r.verified && <p className="text-[10px] tracking-[0.2em] uppercase text-white/30 mt-3">Verified Purchase</p>}
              </div>
            ))}
            {reviews.length === 0 && <p className="text-sm text-white/40">Be the first to review this piece.</p>}
          </div>
        </div>
        <div>
          <p className="label-757 mb-6">Write a Review</p>
          <form onSubmit={submit} className="space-y-5">
            <input
              required
              data-testid="review-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              className="w-full bg-transparent border-b border-white/20 focus:border-[#0FB5A3] outline-none py-3 text-sm text-white placeholder:text-white/30 transition-colors"
            />
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/50 uppercase tracking-[0.2em] mr-2">Rating</span>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`review-star-${n}`}
                  aria-label={`${n} stars`}
                  onClick={() => setForm({ ...form, rating: n })}
                >
                  <Star size={18} className={n <= form.rating ? "text-[#0FB5A3]" : "text-white/20"} fill={n <= form.rating ? "#0FB5A3" : "none"} />
                </button>
              ))}
            </div>
            <textarea
              required
              data-testid="review-comment-input"
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              placeholder="Share your experience..."
              rows={4}
              className="w-full bg-transparent border border-white/20 focus:border-[#0FB5A3] outline-none p-4 text-sm text-white placeholder:text-white/30 transition-colors resize-none"
            />
            <button type="submit" disabled={submitting} className="btn-757-outline" data-testid="review-submit-btn">
              {submitting ? "Submitting..." : "Submit Review"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const { addToCart, isWishlisted, toggleWishlist, trackViewed, recentlyViewed } = useStore();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setSize(null);
    setColor(null);
    setQty(1);
    setActiveImage(0);
    setNotFound(false);
    api
      .get(`/products/${slug}`)
      .then(({ data }) => {
        setProduct(data);
        trackViewed(data);
        if (data.sizes.length === 1) setSize(data.sizes[0]);
        if (data.colors?.length) setColor(data.colors[0].name);
      })
      .catch(() => setNotFound(true));
    api.get(`/products/${slug}/related`).then(({ data }) => setRelated(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (notFound)
    return (
      <div className="pt-48 pb-32 text-center">
        <p className="text-white/50">This piece could not be found.</p>
        <Link to="/shop" className="btn-757 mt-8 inline-flex">Back to Shop</Link>
      </div>
    );

  if (!product)
    return (
      <div className="pt-32 pb-24 max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12">
        <div className="aspect-[4/5] bg-[#0A0A0A] animate-pulse" />
        <div className="space-y-4 pt-8">
          <div className="h-8 w-2/3 bg-[#0A0A0A] animate-pulse" />
          <div className="h-4 w-1/3 bg-[#0A0A0A] animate-pulse" />
        </div>
      </div>
    );

  const handleAdd = () => {
    if (!size) return toast.error("Please select a size");
    if (!product.in_stock) return toast.error("This piece is sold out");
    addToCart(product, size, qty, color);
  };

  const viewedOthers = recentlyViewed.filter((p) => p.id !== product.id);

  return (
    <div className="bg-black pt-28 lg:pt-36 pb-24">
      <SEO
        title={product.name}
        description={product.description}
        schema={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          description: product.description,
          image: product.images[0],
          brand: { "@type": "Brand", name: "7FIFTY7" },
          offers: { "@type": "Offer", price: product.price, priceCurrency: "USD", availability: "https://schema.org/InStock" },
          aggregateRating: product.num_reviews > 0 ? { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: product.num_reviews } : undefined,
        }}
      />
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <nav className="text-[11px] tracking-[0.2em] uppercase text-white/40 mb-8" data-testid="breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link> / <Link to="/shop" className="hover:text-white">Shop</Link> /{" "}
          <span className="text-white/70">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Gallery */}
          <div>
            <ZoomImage src={imgUrl(product.images[activeImage], 1100)} alt={product.name} />
            {product.images.length > 1 && (
              <div className="flex gap-3 mt-4">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`product-thumb-${i}`}
                    onClick={() => setActiveImage(i)}
                    className={`w-20 h-24 overflow-hidden border transition-colors ${i === activeImage ? "border-[#0FB5A3]" : "border-white/10 hover:border-white/40"}`}
                  >
                    <img src={imgUrl(img, 200)} alt={`${product.name} view ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <p className="label-757 mb-3 capitalize">{product.collection} Collection · {product.category}</p>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-4" data-testid="product-title">
              {product.name}
            </h1>
            <div className="flex items-center gap-4 mb-6">
              <p className="text-2xl text-white" data-testid="product-price">{money(product.price)}</p>
              {product.compare_at_price && <p className="text-base text-white/30 line-through">{money(product.compare_at_price)}</p>}
              {product.num_reviews > 0 && (
                <span className="flex items-center gap-1.5 text-xs text-white/50">
                  <Star size={12} className="text-[#0FB5A3]" fill="#0FB5A3" /> {product.rating} ({product.num_reviews})
                </span>
              )}
            </div>
            <p className="text-sm text-white/60 leading-relaxed mb-8">{product.description}</p>

            {/* Color selector */}
            {product.colors?.length > 0 && (
              <div className="mb-7">
                <p className="text-xs tracking-[0.25em] uppercase text-white/60 mb-3">
                  Color{color ? <span className="text-white/40 normal-case tracking-normal ml-2">— {color}</span> : null}
                </p>
                <div className="flex flex-wrap gap-3" data-testid="color-selector">
                  {product.colors.map((c) => (
                    <button
                      key={c.name}
                      data-testid={`color-option-${c.name.replace(/\s/g, "-")}`}
                      aria-label={c.name}
                      onClick={() => setColor(c.name)}
                      className={`w-9 h-9 rounded-full border-2 transition-[transform,border-color] ${
                        color === c.name ? "border-[#0FB5A3] scale-110" : "border-white/20 hover:border-white/60"
                      }`}
                      style={{ backgroundColor: c.hex }}
                      title={c.name}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Size selector */}
            <div className="mb-7">
              <div className="flex justify-between items-center mb-3">
                <p className="text-xs tracking-[0.25em] uppercase text-white/60">Size</p>
                <span className="text-[11px] text-white/30">Size guide available on request</span>
              </div>
              <div className="flex flex-wrap gap-2.5" data-testid="size-selector">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-option-${s.replace(/\s|\//g, "-")}`}
                    onClick={() => setSize(s)}
                    className={`min-w-[52px] px-3 py-2.5 text-xs tracking-wider border transition-colors ${
                      size === s
                        ? "border-[#0FB5A3] bg-[#0FB5A3]/10 text-[#0FB5A3]"
                        : "border-white/20 text-white/60 hover:border-white/60 hover:text-white"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Stock status */}
            {!product.in_stock ? (
              <p className="text-xs tracking-[0.25em] uppercase text-white/40 mb-5" data-testid="stock-soldout">
                Sold out — this batch is gone forever
              </p>
            ) : product.stock <= 7 ? (
              <p className="text-xs tracking-[0.25em] uppercase text-[#C9A24B] mb-5" data-testid="stock-low">
                Only {product.stock} left in this numbered batch
              </p>
            ) : null}

            {/* Quantity + actions */}
            <div className="flex gap-4 mb-5">
              <div className="flex items-center border border-white/20" data-testid="quantity-selector">
                <button data-testid="qty-minus-btn" aria-label="Decrease quantity" onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-4 text-white/60 hover:text-[#0FB5A3]">
                  <Minus size={14} />
                </button>
                <span className="w-10 text-center text-sm text-white" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-plus-btn" aria-label="Increase quantity" onClick={() => setQty(Math.min(10, qty + 1))} className="px-4 py-4 text-white/60 hover:text-[#0FB5A3]">
                  <Plus size={14} />
                </button>
              </div>
              <button
                onClick={handleAdd}
                disabled={!product.in_stock}
                className="btn-757 flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="add-to-cart-btn"
              >
                {product.in_stock ? <>Add to Bag — {money(product.price * qty)}</> : "Sold Out"}
              </button>
              <button
                data-testid="product-wishlist-btn"
                aria-label="Toggle wishlist"
                onClick={() => toggleWishlist(product)}
                className={`px-4 border transition-colors ${
                  isWishlisted(product.id) ? "border-[#0FB5A3] text-[#0FB5A3]" : "border-white/20 text-white/60 hover:border-white/60"
                }`}
              >
                <Heart size={17} strokeWidth={1.5} fill={isWishlisted(product.id) ? "#0FB5A3" : "none"} />
              </button>
            </div>

            <div className="flex flex-wrap gap-5 text-[11px] text-white/40 tracking-wider mb-8">
              <span className="flex items-center gap-1.5"><Truck size={13} className="text-[#0FB5A3]" /> Free shipping over $150</span>
              <span className="flex items-center gap-1.5"><RotateCcw size={13} className="text-[#0FB5A3]" /> 30-day returns</span>
              <span className="flex items-center gap-1.5"><ShieldCheck size={13} className="text-[#0FB5A3]" /> Numbered edition</span>
            </div>

            <Accordion type="single" collapsible className="border-t border-white/10">
              {product.story && (
                <AccordionItem value="story" className="border-white/10">
                  <AccordionTrigger className="text-xs tracking-[0.25em] uppercase text-white/70 hover:text-white hover:no-underline" data-testid="accordion-story">
                    The Story
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-sm text-white/50 leading-relaxed font-serif-757 italic text-base">{product.story}</p>
                  </AccordionContent>
                </AccordionItem>
              )}
              {product.fabric && (
                <AccordionItem value="fabric" className="border-white/10">
                  <AccordionTrigger className="text-xs tracking-[0.25em] uppercase text-white/70 hover:text-white hover:no-underline" data-testid="accordion-fabric">
                    Fabric & Care
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-sm text-white/50 leading-relaxed">{product.fabric}</p>
                  </AccordionContent>
                </AccordionItem>
              )}
              <AccordionItem value="details" className="border-white/10">
                <AccordionTrigger className="text-xs tracking-[0.25em] uppercase text-white/70 hover:text-white hover:no-underline" data-testid="accordion-details">
                  Details & Craftsmanship
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2 text-sm text-white/50">
                    {product.details.map((d) => (
                      <li key={d} className="flex gap-2"><span className="text-[#0FB5A3]">⬡</span> {d}</li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="shipping" className="border-white/10">
                <AccordionTrigger className="text-xs tracking-[0.25em] uppercase text-white/70 hover:text-white hover:no-underline" data-testid="accordion-shipping">
                  Shipping & Returns
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm text-white/50 leading-relaxed">
                    Free worldwide shipping on orders over $150. US delivery in 3-5 business days. 30-day returns on unworn items with tags attached. See our shipping and returns policies for full details.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        <ReviewSection product={product} />

        {/* Related */}
        {related.length > 0 && (
          <section className="border-t border-white/10 pt-16" data-testid="related-products">
            <p className="label-757 mb-3">Complete the Look</p>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white tracking-tight mb-10">You May Also Like</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </section>
        )}

        {/* Recently viewed */}
        {viewedOthers.length > 0 && (
          <section className="border-t border-white/10 pt-16 mt-16" data-testid="recently-viewed">
            <p className="label-757 mb-3">Your Journey</p>
            <h2 className="font-display text-2xl font-semibold text-white tracking-tight mb-10">Recently Viewed</h2>
            <div className="flex gap-5 overflow-x-auto pb-4">
              {viewedOthers.map((p) => (
                <Link key={p.id} to={`/product/${p.slug}`} className="shrink-0 w-44 group" data-testid={`recent-item-${p.slug}`}>
                  <div className="img-zoom aspect-[4/5] bg-[#0A0A0A]">
                    <img src={imgUrl(p.image, 300)} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </div>
                  <p className="text-xs text-white mt-2.5 font-display group-hover:text-[#0FB5A3] transition-colors">{p.name}</p>
                  <p className="text-xs text-white/40 mt-0.5">{money(p.price)}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
